/**
 * Embed icon-font files into a captured snapshot's CSS — localhost only.
 *
 * The portal's icons are font glyphs (keenicons, Font Awesome…), not images.
 * `inlineStylesheet` copies the CSS text into the snapshot, but the
 * `@font-face` rules inside it still point at `url(http://localhost:5173/...)`
 * — unreachable from whoever replays the recording, so every icon renders as
 * an empty box. rrweb's own `collectFonts` does not help: measured against
 * this app, it captures zero CSS-declared fonts (it only hooks the
 * programmatic FontFace API).
 *
 * So this walks the snapshot's inlined CSS and replaces font URLs with data
 * URIs, subject to two hard constraints:
 *
 * - Only families the page actually LOADED (`document.fonts`) — the CSS
 *   declares ~3.5 MB of fonts, most never used on a given page.
 * - A total byte budget. Font binaries do not gzip (already compressed), so
 *   every embedded byte lands almost 1:1 on the wire, and the wire must stay
 *   under the server's 2 MB cap with room for the DOM + CSS itself.
 *
 * Production recordings skip all of this: tenant portals serve fonts from
 * publicly reachable URLs, and embedding would spend wire budget on bytes the
 * viewer can fetch anyway.
 */

/**
 * Total data-URI bytes allowed per snapshot. The snapshot itself compresses to
 * ~300 KB; this keeps the worst case comfortably under the 1.9 MB wire check.
 */
const FONT_BUDGET_BYTES = 1_400_000

/** url(...) candidates inside one @font-face block, capturing the path. */
const URL_RE = /url\((['"]?)([^'")]+)\1\)/g

const MIME_BY_EXT: Record<string, string> = {
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
  eot: 'application/vnd.ms-fontobject',
}

/** Fetched fonts, by absolute URL. `null` marks a fetch that failed — do not retry. */
const fontCache = new Map<string, string | null>()

const toDataUri = async (url: string): Promise<string | null> => {
  const cached = fontCache.get(url)
  if (cached !== undefined) return cached

  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(String(res.status))
    const buf = await res.arrayBuffer()
    const ext = url.split('.').pop()?.split(/[?#]/)[0]?.toLowerCase() ?? ''
    const mime = MIME_BY_EXT[ext] ?? 'application/octet-stream'

    // btoa in slices — spreading a whole font file into one call overflows
    // the argument limit on large files.
    const bytes = new Uint8Array(buf)
    let binary = ''
    for (let i = 0; i < bytes.length; i += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
    }
    const uri = `data:${mime};base64,${btoa(binary)}`
    fontCache.set(url, uri)
    return uri
  } catch {
    fontCache.set(url, null)
    return null
  }
}

/** Font families the page has actually loaded, normalized for comparison. */
const loadedFamilies = (): Set<string> => {
  const out = new Set<string>()
  try {
    document.fonts.forEach((f) => {
      if (f.status === 'loaded') out.add(f.family.replace(/['"]/g, '').trim().toLowerCase())
    })
  } catch {
    // FontFaceSet unavailable — inline nothing rather than everything.
  }
  return out
}

/**
 * Rewrite the @font-face rules in one CSS string. Only same-origin fonts, only
 * loaded families, until the shared budget runs out. Returns the CSS unchanged
 * when there is nothing to do.
 */
const inlineCssFonts = async (css: string, budget: {left: number}): Promise<string> => {
  if (!css.includes('@font-face')) return css

  const families = loadedFamilies()
  if (families.size === 0) return css

  const blocks = css.match(/@font-face\s*\{[^}]*\}/g)
  if (!blocks) return css

  let out = css
  for (const block of blocks) {
    const family = block
      .match(/font-family\s*:\s*(['"]?)([^;'"]+)\1/)?.[2]
      ?.trim()
      .toLowerCase()
    if (!family || !families.has(family)) continue

    // The browser uses the first source it supports; embed that one and let
    // the rest of the src list stand — a data URI first wins at replay.
    let newBlock = block
    for (const m of block.matchAll(URL_RE)) {
      const url = m[2]
      if (!/^https?:\/\//.test(url) && !url.startsWith('/')) continue
      const abs = new URL(url, window.location.origin).href
      if (new URL(abs).origin !== window.location.origin) continue

      const uri = await toDataUri(abs)
      if (!uri || uri.length > budget.left) continue

      budget.left -= uri.length
      newBlock = newBlock.replace(m[0], `url(${uri})`)
      break // one embedded source per @font-face is enough
    }
    if (newBlock !== block) out = out.replace(block, newBlock)
  }
  return out
}

/**
 * Walk a full-snapshot event and inline fonts into every piece of captured
 * CSS (`_cssText` on inlined stylesheets, and `<style>` text nodes). Mutates
 * the event in place; a no-op away from localhost or when nothing matches.
 */
export const inlineFontsInSnapshot = async (
  event: {type: number} & Record<string, unknown>
): Promise<void> => {
  if (window.location.hostname !== 'localhost') return

  const budget = {left: FONT_BUDGET_BYTES}

  const walk = async (node: any): Promise<void> => {
    if (!node || typeof node !== 'object') return
    if (typeof node.attributes?._cssText === 'string') {
      node.attributes._cssText = await inlineCssFonts(node.attributes._cssText, budget)
    }
    // A <style> tag's CSS is an ordinary text child node.
    if (typeof node.textContent === 'string' && node.textContent.includes('@font-face')) {
      node.textContent = await inlineCssFonts(node.textContent, budget)
    }
    if (Array.isArray(node.childNodes)) {
      for (const child of node.childNodes) await walk(child)
    }
  }

  await walk((event as any).data?.node)
}
